// hooks/useScreenAwake.ts
import { useEffect, useRef, useState, useCallback } from 'react';
import NoSleep from 'nosleep.js';

interface UseScreenAwakeReturn {
  isSupported: boolean;
  isActive: boolean;
  enable: () => Promise<void>;
  disable: () => void;
  error: Error | null;
}

export const useScreenAwake = (): UseScreenAwakeReturn => {
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const noSleepRef = useRef<NoSleep | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const reEnableIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize NoSleep
  useEffect(() => {
    noSleepRef.current = new NoSleep();
    return () => {
      if (noSleepRef.current) {
        noSleepRef.current.disable();
      }
    };
  }, []);

  const enable = useCallback(async () => {
    setError(null);
    
    try {
      // Try native Wake Lock first
      if ('wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          setIsActive(true);
          console.log('Native wake lock enabled');
          
          // Re-acquire periodically
          if (reEnableIntervalRef.current) {
            clearInterval(reEnableIntervalRef.current);
          }
          
          reEnableIntervalRef.current = setInterval(async () => {
            if (document.visibilityState === 'visible') {
              try {
                if (!wakeLockRef.current || wakeLockRef.current.released) {
                  wakeLockRef.current = await navigator.wakeLock.request('screen');
                  console.log('Wake lock re-acquired');
                }
              } catch (err) {
                console.error('Failed to re-acquire wake lock:', err);
                // Try NoSleep as fallback
                if (noSleepRef.current) {
                  noSleepRef.current.enable();
                }
              }
            }
          }, 20000); // Every 20 seconds
          
          return;
        } catch (err) {
          console.warn('Native wake lock failed, falling back to NoSleep:', err);
        }
      }
      
      // Fallback to NoSleep
      if (noSleepRef.current) {
        // NoSleep requires user interaction, so it might need to be triggered by a user action
        await noSleepRef.current.enable();
        setIsActive(true);
        console.log('NoSleep enabled');
        
        // Re-enable periodically for NoSleep as well
        if (reEnableIntervalRef.current) {
          clearInterval(reEnableIntervalRef.current);
        }
        
        reEnableIntervalRef.current = setInterval(() => {
          if (noSleepRef.current && document.visibilityState === 'visible') {
            noSleepRef.current.disable();
            setTimeout(() => {
              noSleepRef.current?.enable();
              console.log('NoSleep re-enabled');
            }, 100);
          }
        }, 30000); // Every 30 seconds
      }
    } catch (err) {
      setError(err as Error);
      setIsActive(false);
      console.error('Failed to enable screen wake:', err);
    }
  }, []);

  const disable = useCallback(() => {
    // Clear re-enable interval
    if (reEnableIntervalRef.current) {
      clearInterval(reEnableIntervalRef.current);
      reEnableIntervalRef.current = null;
    }
    
    // Release native wake lock
    if (wakeLockRef.current && !wakeLockRef.current.released) {
      wakeLockRef.current.release().catch(console.error);
      wakeLockRef.current = null;
    }
    
    // Disable NoSleep
    if (noSleepRef.current) {
      noSleepRef.current.disable();
    }
    
    setIsActive(false);
    console.log('Screen wake disabled');
  }, []);

  // Handle page visibility and focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActive) {
        // Re-enable when page becomes visible
        enable();
      }
    };

    const handleFocus = () => {
      if (isActive) {
        enable();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isActive, enable]);

  // Cleanup
  useEffect(() => {
    return () => {
      disable();
    };
  }, [disable]);

  return {
    isSupported: true, // NoSleep works on all browsers
    isActive,
    enable,
    disable,
    error
  };
};